import React, { useState } from "react";
import { motion } from "framer-motion";
import { Radio, Send, X, CheckCircle2 } from "lucide-react";
import { sfx } from "@/lib/sound";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CommsPanel({ onClose }) {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || !form.email.includes("@") || form.message.trim().length < 10) {
      setError("SIGNAL INCOMPLETE — name, valid email and a message of 10+ characters required.");
      sfx.back();
      return;
    }
    setError("");
    setStatus("sending");
    sfx.clank();
    try {
      const res = await fetch(`${API}/api/comms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("done");
      sfx.trophy();
    } catch {
      setStatus("idle");
      setError("TRANSMISSION FAILED — please retry.");
      sfx.back();
    }
  };

  return (
    <motion.div
      className="absolute inset-0 z-40 flex items-center justify-center p-4 sm:p-8"
      data-testid="comms-panel"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-[#F1F5F9]/70 backdrop-blur-md"
        data-testid="comms-backdrop"
      />
      <motion.div
        className="iv-glass iv-overlay-card relative w-full max-w-lg overflow-hidden rounded-2xl"
        initial={{ scale: 0.93, y: 24 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 16, opacity: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 26 }}
      >
        <div className="flex items-center justify-between border-b border-white/70 px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0284C7]/10">
              <Radio size={16} color="#0284C7" />
            </span>
            <div>
              <div className="f-syncopate text-[11px] tracking-[0.3em] text-[#0F172A]">OPEN COMMS</div>
              <div className="f-mono text-[8px] tracking-[0.25em] text-[#94A3B8]">SECURE CHANNEL · INVESTORS &amp; PARTNERS</div>
            </div>
          </div>
          <button
            type="button"
            data-testid="comms-close-btn"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#CBD5E1] text-[#475569] transition-colors hover:border-[#38BDF8] hover:text-[#0284C7]"
          >
            <X size={15} />
          </button>
        </div>

        {status === "done" ? (
          <div className="flex flex-col items-center gap-4 px-6 py-12 text-center" data-testid="comms-success">
            <CheckCircle2 size={40} color="#0284C7" />
            <div className="f-orbitron text-lg font-bold text-[#0F172A]">TRANSMISSION RECEIVED</div>
            <p className="max-w-xs text-sm text-[#64748B]">
              Your signal has reached the IDEVRA command deck. We will respond on secure channels.
            </p>
            <button
              type="button"
              data-testid="comms-done-close-btn"
              onClick={onClose}
              className="f-syncopate mt-2 rounded-full bg-[#0284C7] px-6 py-3 text-[10px] tracking-[0.25em] text-white hover:bg-[#0369A1] transition-colors"
            >
              CLOSE CHANNEL
            </button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="flex flex-col gap-3 px-6 py-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                data-testid="comms-name-input"
                className="iv-input"
                placeholder="OPERATOR NAME *"
                value={form.name}
                onChange={set("name")}
              />
              <input
                data-testid="comms-email-input"
                className="iv-input"
                type="email"
                placeholder="EMAIL *"
                value={form.email}
                onChange={set("email")}
              />
            </div>
            <input
              data-testid="comms-org-input"
              className="iv-input"
              placeholder="ORGANIZATION (OPTIONAL)"
              value={form.organization}
              onChange={set("organization")}
            />
            <textarea
              data-testid="comms-message-input"
              className="iv-input min-h-[110px] resize-none"
              placeholder="TRANSMISSION *"
              value={form.message}
              onChange={set("message")}
            />
            {error && (
              <div className="f-mono text-[9px] tracking-[0.15em] text-[#D97706]" data-testid="comms-error">
                {error}
              </div>
            )}
            <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
              <span className="f-mono text-[8px] tracking-[0.2em] text-[#94A3B8]">
                DIRECT LINE: ceo@toothrocket.com
              </span>
              <button
                type="submit"
                data-testid="comms-submit-btn"
                disabled={status === "sending"}
                className="f-syncopate inline-flex items-center gap-2 rounded-full bg-[#0284C7] px-6 py-3 text-[10px] tracking-[0.25em] text-white shadow-[0_0_24px_rgba(56,189,248,0.5)] transition-colors hover:bg-[#0369A1] disabled:opacity-60"
              >
                {status === "sending" ? "TRANSMITTING…" : "TRANSMIT"} <Send size={12} />
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
}
